import { API_ROUTES } from '@/api/apiRoutes';
import { useWorkflowStore } from '@/state/workflow.state';
import { safeAxios } from '@/services/safeAxios';
import type { NodeType } from '@/enums/node-type.enum';
import type { DesignAppData, DesignAppInput } from '@/types/design-app.types';
import type { ResolvedAssets } from '@/types/api/assets';
import type { ToolConfigOutputExample } from '@/types/tool-config.types';
import type { ImageFile } from 'web';

export type ToolOutputType = 'image' | 'video' | 'text' | 'any';

export interface ToolLoraOption {
  id: string;
  name: string;
  file: string;
  coverImage?: ImageFile;
}

// `defaultValue` is already in the FE DesignAppInput `value` shape for the type
// (e.g. { prompt }, { file }, a number, or a seed { isRandom, seed }).
export interface ToolInput {
  nodeId: string;
  type: NodeType;
  order: number;
  exposed: boolean;
  isIterator?: boolean;
  acceptedFileType?: 'image' | 'video';
  values?: (string | number)[];
  numberRange?: { min?: number; max?: number };
  seedConfig?: { isRandom: boolean; seed?: number };
  // Set only on MultiLora inputs; `loraOptions` is omitted in single mode.
  loraMode?: 'single' | 'selector';
  loraOptions?: ToolLoraOption[];
  name?: string;
  description?: string | null;
  required?: boolean;
  defaultValue?: unknown;
}

export interface ToolOutput {
  nodeId: string;
  outputType?: ToolOutputType;
  examples?: ToolConfigOutputExample[];
  inputs: ToolInput[];
}

export interface ToolRunData {
  workflowId: string;
  recipeVersion: number;
  outputs: ToolOutput[];
  /** Without a tool config, an input's `required` is never set. Absent from an older server. */
  hasToolConfig?: boolean;
  resolvedAssets?: ResolvedAssets;
}

export const getToolRunData = async (toolId: string, version?: number): Promise<ToolRunData> => {
  const res = await safeAxios.get<ToolRunData>(
    API_ROUTES.tools.runData({ toolId }),
    version !== undefined ? { params: { version } } : undefined,
  );
  useWorkflowStore.getState().mergeResolvedAssets(res.data.resolvedAssets ?? {});
  return res.data;
};

export const getToolUserData = async (toolId: string, version: number): Promise<DesignAppData> => {
  const res = await safeAxios.get<DesignAppData>(API_ROUTES.tools.userData({ toolId }), { params: { version } });
  useWorkflowStore.getState().mergeResolvedAssets(res.data.resolvedAssets ?? {});
  return res.data;
};

export const saveToolUserData = async (
  toolId: string,
  inputs: Array<Pick<DesignAppInput, 'id' | 'type' | 'value'>>,
): Promise<void> => {
  await safeAxios.post(API_ROUTES.tools.userData({ toolId }), { designApp: inputs });
};
